import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { IMAGES } from "./data";
import { useScrollTo } from "./primitives";

const LINES = ["India's First Smart Pipeline", "Design Platform", "for Farmers"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.5]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroAerial}
          alt="Aerial view of agricultural fields"
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* blue gradient overlay */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-gradient-to-t from-[#0e3f70]/95 via-[#1a6fc4]/55 to-[#1a6fc4]/25"
      />
      <div className="absolute inset-0 z-10 pm-grid-blue opacity-40" />

      <div className="relative z-20 mx-auto w-full max-w-7xl px-5 pb-20 pt-32 sm:px-8 sm:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-4 flex"
        >
          <span
            data-testid="hero-coming-soon-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-[#a6d47a]/45 bg-[#5aa72f]/20 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-[#d3ecb6] backdrop-blur-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#a6d47a] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#a6d47a]" />
            </span>
            PipeMitra App &mdash; Coming Soon
          </span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white backdrop-blur-sm"
        >
          <MapPin className="h-3.5 w-3.5 text-[#a6d47a]" />
          AgriTech &times; Pipeline Engineering &times; India
        </motion.div>

        <h1 className="font-heading text-4xl font-black leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden py-0.5">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-7 max-w-2xl text-lg leading-relaxed text-white/85 sm:text-xl"
        >
          Smart agricultural pipeline planning powered by digital mapping,
          elevation data and hydraulic engineering.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-discover-btn"
            onClick={() => scrollTo("about")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-[#0e3f70] transition-all duration-300 hover:bg-[#eaf3fb] active:scale-[0.98]"
          >
            Discover PipeMitra
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-contact-btn"
            onClick={() => scrollTo("contact")}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/15 active:scale-[0.98]"
          >
            <Mail className="h-5 w-5" />
            Contact Us
          </button>
        </motion.div>
      </div>
    </section>
  );
};
